import { useState, useCallback } from 'react';
import { ElementType, ElementTypeLabels, ElementWithUserNames } from '../../../../types/element.types';
import { SceneInfo } from '../../../../types/scene.types';
import { getAllElementsInBreakdownCache } from '../../../../components/ElementSearch/getAllElementsInBreakdownCache';
import { useDataCache } from '../../../../contexts/BreakdownDataCacheContext';

/**
 * 场次元素重名检查 Hook
 * 
 * 功能：
 * - 检查元素名称和简称是否重复
 * - 支持按字段检查（name/description）
 * - 获取所有场次元素（使用缓存）
 * - 过滤重名元素
 * - 更新重名检查结果状态
 */
export const useElementDuplicateCheck = (sceneInfo: SceneInfo | null) => {
  const { getElementCache } = useDataCache();
  const [duplicateCheckResults, setDuplicateCheckResults] = useState<Map<string, { hasDuplicate: boolean; duplicateElements: any[] }>>(new Map());

  const checkDuplicateName = useCallback(async (
    elementId: string,
    elementName: string,
    elementAbbr?: string,
    elementType?: ElementType,
    checkField?: 'name' | 'description'
  ): Promise<boolean> => {
    // 如果既没有名称也没有简称，或者没有 sceneBelongBreakdownID，返回 false
    if ((!elementName?.trim() && !elementAbbr?.trim()) || !sceneInfo?.sceneBelongBreakdownID) {
      return false;
    }
    
    try {
      // 获取场次表关联的所有元素（优先使用缓存）
      const allElements = await getAllElementsInBreakdownCache(sceneInfo.sceneBelongBreakdownID, getElementCache);
      
      const duplicateElements: Array<{
        name: string;
        abbreviation?: string;
        type: ElementType;
        typeLabel: string;
      }> = [];

      // 根据 checkField 参数决定检查哪些字段
      // 如果指定了 checkField，只检查对应字段；否则检查所有字段
      let nameDuplicates: ElementWithUserNames[] = [];
      let abbreviationDuplicates: ElementWithUserNames[] = [];

      // 检查元素名称重名（如果提供了名称，且 checkField 为 'name' 或未指定）
      if ((!checkField || checkField === 'name') && elementName?.trim()) {
        nameDuplicates = allElements.filter((item: ElementWithUserNames) => 
          item.elementID !== elementId && // 排除当前编辑的元素
          item.elementName?.trim() === elementName.trim() && 
          item.elementType === elementType
        );
      }

      // 检查简称重名（如果提供了简称，且 checkField 为 'description' 或未指定）
      if ((!checkField || checkField === 'description') && elementAbbr?.trim()) {
        abbreviationDuplicates = allElements.filter((item: ElementWithUserNames) => 
          item.elementID !== elementId && // 排除当前编辑的元素
          item.elementAbbr?.trim() === elementAbbr.trim() && 
          item.elementType === elementType
        );
      }

      // 合并重名元素
      const allDuplicates = [...nameDuplicates, ...abbreviationDuplicates];
      const uniqueDuplicates = allDuplicates.filter((item, index, self) => 
        index === self.findIndex(t => t.elementID === item.elementID)
      );

      uniqueDuplicates.forEach(item => {
        const itemType = item.elementType || elementType;
        if (itemType) {
          duplicateElements.push({
            name: item.elementName || '',
            abbreviation: item.elementAbbr,
            type: itemType,
            typeLabel: ElementTypeLabels[itemType]
          });
        }
      });

      const hasDuplicate = duplicateElements.length > 0;
      
      // 更新重名检查结果
      setDuplicateCheckResults(prev => {
        const newMap = new Map(prev);
        newMap.set(elementId, { hasDuplicate, duplicateElements });
        return newMap;
      });

      return hasDuplicate;
    } catch (error) {
      console.error('❌ 重名检查失败:', error);
      return false;
    }
  }, [sceneInfo, getElementCache]);

  return {
    checkDuplicateName,
    duplicateCheckResults,
  };
};

